import React, { Fragment } from 'react';
import { Row, Col } from 'antd';
import _camelCase from 'lodash/camelCase';
import _isEqual from 'lodash/isEqual';
import _isNull from 'lodash/isNull';
import _isObject from 'lodash/isObject';
import _isString from 'lodash/isString';
import _startCase from 'lodash/startCase';
import _union from 'lodash/union';
import _upperFirst from 'lodash/upperFirst';

import { TCustomerDetails } from 'containers/Customer/i18n/TModules';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import i18n from 'utils/i18n';

import {
  audienceNameLabel,
  classifyFileChange,
  criterionLabel,
  CUSTOM_ATTRIBUTES_KEY,
  decomposeCustomAttributes,
  FILE_PREFIX,
  FILTER_PREFIX,
  fileActionText,
  formatCriterionValue,
  IAudienceDetailLine,
  isAudienceEntity,
  NAME_KEY
} from '../audience';
import { hasValue } from '../changes';
import Cell from './Cell';

// Audience rows build their own columns instead of going through `renderRow`, which
// wraps every value in `Cell` and would land an object on Cell's JSON fallback.
const renderValues = (values: string[]) =>
  values.length === 0 ? '-' : values.map((value, idx) => <div key={idx}>{value}</div>);

const renderDetailLines = (lines: IAudienceDetailLine[]) => (
  <>
    {lines.map((line, idx) => (
      <Row gutter={16} key={idx} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
        <Col span={4} style={{ fontWeight: 600 }}>
          {line.label}
        </Col>
        <Col span={10}>{renderValues(line.before)}</Col>
        <Col span={10}>{renderValues(line.after)}</Col>
      </Row>
    ))}
  </>
);

// PLA-9811: the previous helper walked only the NEW value, so a key or array index that
// the new value dropped never surfaced, and an equal pair produced no keys at all -
// both rendered the Details/Old/New panel with a header and nothing under it.
const changedKeys = (oldVal, newVal) => {
  const keys = _union(Object.keys(oldVal || {}), Object.keys(newVal || {}));
  return keys.filter(key => !_isEqual(oldVal?.[key], newVal?.[key]));
};

const renderPropertyLabel = (key, t) => {
  const labelKey = `${TCustomerDetails}.AuditLog.DetailsColumn.${_upperFirst(_camelCase(key))}`;
  return _isString(key) && i18n.exists(labelKey) ? t(labelKey) : _startCase(key);
};

const renderRow = (key, data) => {
  // PLA-9811: a truthiness test rendered a real `false` or `0` as a dash.
  return <Col span={10}>{hasValue(data) ? <Cell propertyName={key} data={data} /> : '-'}</Col>;
};

interface ChangesRowProps {
  propertyName: string;
  data: (number | string | null | object | boolean)[];
  entity?: string;
}

const ChangesRow = ({ data, propertyName, entity }: ChangesRowProps) => {
  const { t } = useMultiLanguageTranslation();

  const [oldVal, newVal] = data;

  if (isAudienceEntity(entity)) {
    // Exact key first: `filter_custom_attributes` also matches the FILTER_PREFIX test
    // below, and the prefix branch would render its pruned shape as one generic line.
    if (propertyName === CUSTOM_ATTRIBUTES_KEY) {
      return renderDetailLines(decomposeCustomAttributes(data));
    }

    if (propertyName.startsWith(FILE_PREFIX)) {
      const fileChange = classifyFileChange(data);
      if (fileChange) {
        return renderDetailLines([
          {
            label: `${fileChange.fileName} ${fileActionText(fileChange)}`.trim(),
            before: fileChange.fromRelation ? [fileChange.fromRelation] : [],
            after: fileChange.toRelation ? [fileChange.toRelation] : []
          }
        ]);
      }
    }

    if (propertyName.startsWith(FILTER_PREFIX)) {
      return renderDetailLines([
        {
          label: criterionLabel(propertyName),
          before: formatCriterionValue(oldVal),
          after: formatCriterionValue(newVal)
        }
      ]);
    }

    if (propertyName === NAME_KEY) {
      return renderDetailLines([
        {
          label: audienceNameLabel(),
          before: formatCriterionValue(oldVal),
          after: formatCriterionValue(newVal)
        }
      ]);
    }
  }

  if (propertyName === 'cards') {
    const formatCardsText = cards => {
      if (!Array.isArray(cards) || cards.length === 0) return '-';
      return cards
        .map((card, idx) => {
          if (!card || typeof card !== 'object') return `-`;
          const name = card.name || '-';
          const id = card.id !== undefined && card.id !== null ? card.id : '-';
          return `Card Name: ${name}, id: ${id}`;
        })
        .join('\n');
    };
    const oldCardDisplay = formatCardsText(oldVal);
    const newCardDisplay = formatCardsText(newVal);
    return (
      <Row gutter={16} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
        <Col span={4} style={{ fontWeight: 600 }}>
          Applicable Cards
        </Col>
        <Col span={10}>
          {oldCardDisplay.split('\n').map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </Col>
        <Col span={10}>
          {newCardDisplay.split('\n').map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </Col>
      </Row>
    );
  }

  if (_isObject(oldVal) && _isObject(newVal) && propertyName !== 'properties') {
    // handles things like `display_properties` where each instant is an object
    // objects will contain the ever key and value paris, we only want to display key,values that have been altered
    const keys = changedKeys(oldVal, newVal);

    // Nothing differs key-by-key - the backend still reported the attribute as changed
    // (e.g. a re-serialised but equal hash), so show the values rather than an empty panel.
    if (!keys.length) {
      return (
        <Row gutter={16} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <Col span={4} style={{ fontWeight: 600 }}>
            {renderPropertyLabel(propertyName, t)}
          </Col>
          {renderRow(propertyName, oldVal)}
          {renderRow(propertyName, newVal)}
        </Row>
      );
    }

    return (
      <Row gutter={16} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
        {keys.map(key => {
          const oldV = oldVal?.[key] ?? null;
          const newV = newVal?.[key] ?? null;
          return (
            <Fragment key={key}>
              <Col span={4} style={{ fontWeight: 600 }}>
                {_isString(key) &&
                i18n.exists(
                  `${TCustomerDetails}.AuditLog.DetailsColumn.${_upperFirst(_camelCase(key))}`
                )
                  ? t(`${TCustomerDetails}.AuditLog.DetailsColumn.${_upperFirst(_camelCase(key))}`)
                  : _startCase(key)}
              </Col>
              {renderRow(key, oldV)}
              {renderRow(key, newV)}
            </Fragment>
          );
        })}
      </Row>
    );
  }

  // [null, { xxx: yyy }]
  if (_isObject(newVal) && (_isNull(oldVal) || !_isObject(oldVal))) {
    return (
      <>
        {Object.keys(newVal).map(key => {
          return (
            <Row gutter={16} key={key} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
              <Col span={4} style={{ fontWeight: 600 }}>
                {_isString(key) &&
                i18n.exists(
                  `${TCustomerDetails}.AuditLog.DetailsColumn.${_upperFirst(_camelCase(key))}`
                )
                  ? t(`${TCustomerDetails}.AuditLog.DetailsColumn.${_upperFirst(_camelCase(key))}`)
                  : _startCase(key)}
              </Col>
              {renderRow(key, oldVal)}
              {renderRow(key, newVal[key])}
            </Row>
          );
        })}
      </>
    );
  } else if (_isObject(oldVal) && (_isNull(newVal) || !_isObject(newVal))) {
    // [{ xxx: yyy }, null]
    return (
      <>
        {Object.keys(oldVal).map(key => {
          return (
            <Row gutter={16} key={key} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
              <Col span={4} style={{ fontWeight: 600 }}>
                {_isString(key) &&
                i18n.exists(
                  `${TCustomerDetails}.AuditLog.DetailsColumn.${_upperFirst(_camelCase(key))}`
                )
                  ? t(`${TCustomerDetails}.AuditLog.DetailsColumn.${_upperFirst(_camelCase(key))}`)
                  : _startCase(key)}
              </Col>
              {renderRow(key, oldVal[key])}
              {renderRow(key, newVal)}
            </Row>
          );
        })}
      </>
    );
  } else {
    // [xxx, yyy]
    return (
      <Row gutter={16} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
        <Col span={4} style={{ fontWeight: 600 }}>
          {_isString(propertyName) &&
          i18n.exists(
            `${TCustomerDetails}.AuditLog.DetailsColumn.${_upperFirst(_camelCase(propertyName))}`
          )
            ? t(
                `${TCustomerDetails}.AuditLog.DetailsColumn.${_upperFirst(
                  _camelCase(propertyName)
                )}`
              )
            : _startCase(propertyName)}
        </Col>
        {renderRow(propertyName, oldVal)}
        {renderRow(propertyName, newVal)}
      </Row>
    );
  }
};

export default ChangesRow;
